import { Box } from "@material-ui/core";
import React, { useMemo, useState } from "react";
import {
  BookDemoModal,
  SubscriptionCatalogModal,
  useSubscriptionPurchase,
} from "../../Components/SubscriptionCatalog";
import {
  highlightMatchesPlan,
  planCopy,
  priceUnitLabel,
} from "../../Data/billingPlans";
import { subscribePromptModel } from "../../Data/subscriptionBundles";
import { CcButton } from "../../design-system/carecentre";
import BundleNameList from "./BundleNameList";
import { useStyles } from "./style";

const SubscribePrompt = ({ items = [], subtitle }) => {
  const classes = useStyles();
  const model = useMemo(() => subscribePromptModel(items), [items]);
  const { bundles, names, price, opened } = model;
  const highlightItem = Array.isArray(items) ? items[0] : items;
  const [catalogOpen, setCatalogOpen] = useState(false);
  const { plans, busy, loading, refreshPlans } =
    useSubscriptionPurchase();

  const matchingPlan = useMemo(() => {
    const list = plans || [];
    const direct = list.find((plan) =>
      highlightMatchesPlan(plan, highlightItem),
    );
    if (direct) return direct;
    const bundleCodes = new Set(bundles.map((bundle) => bundle.code));
    return list.find((plan) => bundleCodes.has(plan.code)) || null;
  }, [bundles, highlightItem, plans]);

  const displayNames = matchingPlan ? [matchingPlan.name] : names;
  const moduleName = displayNames.join(" or ");
  const displayPrice = matchingPlan ? matchingPlan.price : price;
  const [scheduling, setScheduling] = useState(false);
  const [demoModuleName, setDemoModuleName] = useState(moduleName);

  const unitLabel = matchingPlan
    ? priceUnitLabel(matchingPlan).replace(/^\s/, "")
    : "/ bed / month";

  const trialDays = Number(matchingPlan?.trial_days);
  const hasTrial = Number.isFinite(trialDays) && trialDays > 0;

  const copy = useMemo(() => {
    if (!matchingPlan) {
      return {
        html: "",
        description: model.tagline,
        features: bundles.flatMap((b) => b.highlights || []),
      };
    }
    const pc = planCopy(matchingPlan);
    if (!pc.html && !pc.description) {
      return {
        html: "",
        description: model.tagline,
        features:
          pc.features.length > 0
            ? pc.features
            : bundles.flatMap((b) => b.highlights || []),
      };
    }
    return pc;
  }, [matchingPlan, model.tagline, bundles]);

  return (
    <Box className={classes.container}>
      <section className={classes.panel}>
        <div className={classes.copy}>
          <div className={classes.headerRow}>
            <span className={classes.eyebrow}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Subscription required
            </span>

            {opened.length > 0 ? (
              <span className={classes.openedChip}>
                You opened <strong>{opened.join(", ")}</strong>
              </span>
            ) : null}
          </div>

          <h1 className={classes.display}>
            <BundleNameList
              names={displayNames}
              className={classes.displayName}
            />
          </h1>

          {copy.html ? (
            <div
              className={classes.planHtml}
              dangerouslySetInnerHTML={{ __html: copy.html }}
            />
          ) : copy.description ? (
            <p className={classes.lead}>{copy.description}</p>
          ) : (
            <p className={classes.lead}>
              You’re not subscribed to this module.
            </p>
          )}

          {subtitle ? <p className={classes.sub}>{subtitle}</p> : null}

          {!copy.html && copy.features.length > 0 ? (
            <div className={classes.featuresSection}>
              <ul className={classes.featuresList}>
                {copy.features.map((feat) => (
                  <li key={feat} className={classes.featureItem}>
                    {feat}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <aside className={classes.aside}>
          <div className={classes.asideTop}>
            <div className={classes.asideHeaderRow}>
              {displayPrice != null ? (
                <span className={classes.priceFrom}>From</span>
              ) : (
                <span />
              )}

              {hasTrial ? (
                <span className={classes.trialBadge}>
                  {trialDays}-day free trial
                </span>
              ) : null}
            </div>

            {displayPrice != null ? (
              <p className={classes.priceLine}>
                <strong>£{displayPrice}</strong>
                <span className={classes.priceUnit}>{unitLabel}</span>
              </p>
            ) : null}
          </div>

          <div className={classes.asideActions}>
            <CcButton
              variant="primary"
              size="lg"
              className={classes.asideBtn}
              onClick={() => setCatalogOpen(true)}
              disabled={busy}
            >
              Buy
            </CcButton>

            <CcButton
              variant="secondary"
              size="lg"
              className={classes.asideBtn}
              onClick={() => {
                setDemoModuleName(moduleName);
                setScheduling(true);
              }}
            >
              Book a demo
            </CcButton>

            {plans && plans.length > 1 ? (
              <button
                type="button"
                className={classes.allPlansLink}
                onClick={() => setCatalogOpen(true)}
              >
                View all plans →
              </button>
            ) : null}
          </div>
        </aside>
      </section>

      <SubscriptionCatalogModal
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        onOpen={refreshPlans}
        plans={plans}
        highlightItem={highlightItem}
        busy={busy}
        loading={loading}
        onBookDemo={(pkg) => {
          setCatalogOpen(false);
          setDemoModuleName(pkg?.name || moduleName);
          setScheduling(true);
        }}
      />
      <BookDemoModal
        open={scheduling}
        onClose={() => setScheduling(false)}
        moduleName={demoModuleName || moduleName}
      />
    </Box>
  );
};

export default SubscribePrompt;
